/**
 * THE REVIEW PORTAL — its own module in the hub.
 *
 * Brian: "Timing on a review is imperative. When people are excited when they
 * receive their products is when we want to get a review. It is NOT prior to
 * receiving the product because that comes off as lazy... They have never
 * experienced the delivery." And: "reviews should have its own portal, but
 * everything should be interconnected and it shouldn't be associated with
 * solely finance."
 *
 * So the first screen is TODAY'S ASKS, ordered by how fresh the delivery is —
 * not a ledger of what already happened. Nothing appears here until the
 * customer is physically holding the product, and payment never gates it.
 *
 * The backend refuses a too-early ask with a 409. This page shows that refusal
 * as an explanation rather than an error, because it is the rule working.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import PageHeader from '../components/PageHeader.jsx'
import Segmented from '../components/Segmented.jsx'
import { api, ApiError } from '../lib/api.js'
import { useAccess } from '../lib/access.jsx'
import { useAuth } from '../lib/auth.jsx'
import { ACTIONS } from '../lib/rbac.js'
import AskQueue from '../features/reviews/AskQueue.jsx'
import ReviewList from '../features/reviews/ReviewList.jsx'
import ContentShelf from '../features/reviews/ContentShelf.jsx'
import Scoreboard from '../features/reviews/Scoreboard.jsx'
import DispatchBoard from '../features/reviews/DispatchBoard.jsx'
import LinksPanel from '../features/reviews/LinksPanel.jsx'
import DateFilter from '../components/DateFilter.jsx'
import { useDateFilter } from '../lib/useDateFilter.js'
import styles from './Reviews.module.css'

const TABS = [
  { key: 'queue', label: 'Ask today' },
  { key: 'outstanding', label: 'Waiting' },
  { key: 'landed', label: 'Landed' },
  { key: 'shelf', label: 'Content shelf' },
  { key: 'scoreboard', label: 'By rep' },
  /* THE MANAGER'S OUTBOX. Deliberately next to "By rep", which answers how each
   * rep is PERFORMING; this one answers what each rep must DO and hands over
   * the message to send. Two different questions that both start "by rep", so
   * they sit together rather than a screen apart. It loads its own four feeds,
   * so it is not in `load()` below. */
  { key: 'dispatch', label: 'Dispatch' },
  { key: 'links', label: 'Links' },
]

export default function Reviews() {
  const access = useAccess()
  const can = access?.can || (() => true)
  /* The queue defaults to the signed-in rep's own book, so the page needs to
   * know who that is. `reviewScope.js` decides whether the DTO is usable and
   * AskQueue renders an unresolvable identity as an explanation rather than as
   * an empty queue — an empty list reads as "no work today" and gets acted on. */
  const { user } = useAuth()

  const [tab, setTab] = useState('queue')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)

  const load = useCallback(async (which = tab) => {
    /* Two panels load themselves: the links form, and the dispatch board, which
     * reads FOUR feeds and settles each independently so one bad route costs
     * its own section rather than the whole screen. Routing either through the
     * single-request switch below would flatten that. */
    if (which === 'links' || which === 'dispatch') { setLoading(false); setError(null); setData({}); return }
    setLoading(true); setError(null)
    try {
      const d =
        which === 'queue' ? await api.reviewQueue()
        : which === 'shelf' ? await api.reviewShelf({ status: 'captured' })
        : which === 'scoreboard' ? await api.reviewScoreboard()
        : await api.reviews({ filter: which })
      setData(d)
    } catch (e) {
      setError(e instanceof ApiError && e.missingRoute
        ? 'The review portal is not deployed on the backend yet.'
        : (e?.message || 'Could not load.'))
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [tab])

  useEffect(() => { load(tab) }, [tab, load])

  /* `action` is the UNDO. A governance flag that cannot be taken back in the
   * same gesture is a governance flag people are afraid to use, so the notice
   * carries the reversal rather than pointing at a record somewhere else. */
  const say = useCallback((msg, tone = 'good', action = null) => {
    setNotice({ msg, tone, action })
    window.setTimeout(() => setNotice(null), action ? 20000 : 9000)
  }, [])

  /* The 409 is the rule working, not a failure. It is surfaced with the
   * backend's own reason, which names who is actually holding the product. */
  const onAsk = useCallback(async (orderId, type) => {
    try {
      const r = await api.reviewAsk({ orderId, type })
      say(r.action === 'followup'
        ? 'Chase recorded. The ask count is unchanged.'
        : `Ask recorded${r.askLagDays != null ? ` — ${r.askLagDays}d after delivery.` : '.'}${r.note ? ' ' + r.note : ''}`)
      load('queue')
    } catch (e) {
      const body = e?.body || {}
      if (e?.status === 409) {
        say(body.reason || body.detail || 'Not askable yet.', 'hold')
      } else {
        say(e?.message || 'Could not record the ask.', 'bad')
      }
    }
  }, [load, say])

  const counts = useMemo(() => {
    if (tab !== 'queue' || !data?.tally) return {}
    return { queue: data.count }
  }, [tab, data])

  const tabs = useMemo(
    () => TABS.map((t) => ({ ...t, count: counts[t.key] })),
    [counts]
  )

  /* THE SHARED DATE SEARCH (2026-10-26, [Jarvis · Claude]) on the three list
   * tabs, by DELIVERED date (the ask queue) / received date (waiting, landed).
   * A row with no date stays, flagged in the summary. The other tabs are a
   * shelf, a rollup and two forms, not dated lists. */
  const df = useDateFilter('reviews')
  const listKey = tab === 'queue' ? 'queue' : (tab === 'outstanding' || tab === 'landed') ? 'reviews' : null
  const dated = useMemo(() => (listKey && Array.isArray(data?.[listKey]) ? df.apply(data[listKey]) : null), [listKey, data, df])
  const shown = useMemo(() => (dated ? { ...data, [listKey]: dated.rows } : data), [dated, data, listKey])

  return (
    <>
      <PageHeader
        eyebrow="Reviews"
        title="Ask while they’re still holding it"
        lead="A review is worth asking for at exactly one moment: when the rings are in their hands and the photos are still going up. Nothing reaches this queue before delivery, and payment never gates it."
      />

      <Segmented options={tabs} value={tab} onChange={setTab} ariaLabel="Review views" />

      {notice && (
        <div className={`${styles.notice} ${styles['notice_' + notice.tone]}`} role="status">
          {notice.msg}
          {notice.action && (
            <button
              type="button"
              className={styles.noticeAction}
              onClick={() => { setNotice(null); notice.action.run() }}
            >
              {notice.action.label}
            </button>
          )}
        </div>
      )}

      {error && <div className={styles.error} role="alert">{error}</div>}

      {loading && <div className={styles.loading}>Loading…</div>}

      {!loading && !error && data && dated && (
        <DateFilter {...df.props} summary={dated.summary} noun={tab === 'queue' ? 'deliveries' : 'reviews'} />
      )}

      {!loading && !error && data && (
        <>
          {tab === 'queue' && (
            <AskQueue
              data={shown}
              user={user}
              canAsk={can(ACTIONS.REVIEWS_ASK)}
              onAsk={onAsk}
              onSuppress={can(ACTIONS.REVIEWS_PUBLISH)
                ? async (orderId, reason) => {
                    try {
                      await api.reviewSuppress(orderId, reason)
                      say(
                        'Flagged do-not-ask. Nobody will be asked to review this account.',
                        'good',
                        {
                          label: 'Undo',
                          run: async () => {
                            try { await api.reviewUnsuppress(orderId); say('Flag lifted — the account is back in the queue.'); load('queue') }
                            catch (e) { say(e?.message || 'Could not lift the flag.', 'bad') }
                          },
                        }
                      )
                      load('queue')
                    } catch (e) { say(e?.message || 'Could not flag.', 'bad') }
                  }
                : null}
              onReload={() => load('queue')}
            />
          )}

          {(tab === 'outstanding' || tab === 'landed') && (
            <ReviewList
              data={shown}
              filter={tab}
              canVerify={can(ACTIONS.REVIEWS_VERIFY)}
              onChase={async (id) => {
                try { const r = await api.reviewFollowup(id); say(`Chase ${r.followups} recorded.`); load(tab) }
                catch (e) { say(e?.message || 'Could not record.', 'bad') }
              }}
              onReceived={async (id, payload) => {
                try { const r = await api.reviewReceived(id, payload); say(r.verification || 'Recorded.'); load(tab) }
                catch (e) { say(e?.body?.reason || e?.message || 'Could not record.', 'bad') }
              }}
              onVerify={async (id) => {
                try { await api.reviewVerify(id); say('Verified.'); load(tab) }
                catch (e) { say(e?.body?.reason || e?.message || 'Could not verify.', 'bad') }
              }}
            />
          )}

          {tab === 'shelf' && (
            <ContentShelf
              data={data}
              canPublish={can(ACTIONS.REVIEWS_PUBLISH)}
              onUsage={async (id, status) => {
                try { await api.reviewUsage(id, status); say('Updated.'); load('shelf') }
                catch (e) { say(e?.body?.reason || e?.message || 'Could not update.', 'bad') }
              }}
            />
          )}

          {tab === 'scoreboard' && <Scoreboard data={data} />}

          {tab === 'dispatch' && <DispatchBoard />}

          {tab === 'links' && <LinksPanel onSaved={() => say('Destinations saved.')} />}
        </>
      )}
    </>
  )
}
